import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {hasPermission, Permissions, user} from '../../../../store/reducers/user';
import Formatter from '../../../../util/formatter';
import {ctxCounterVisitsED} from '../../../../store/reducers/patientContext';
import {useEffect, useRef, useState} from 'react';
import _ from 'lodash';
import {getSafeguardingValues} from '../formsUtil';
import Notes from '../../../../util/notes';
import moment from 'moment/moment';
import uuidv4 from 'uuid/v4';
import {Checkbox, FormControl, FormControlLabel, Grid, Paper, RadioGroup} from '@mui/material';
import Radio from '@mui/material/Radio';
import InformationBox from '../../../../components/other/informationBox';
import clsx from 'clsx';
import {Safeguarding} from '../../../../controls/agyle/ecds';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import {getConfig} from '../../../../util/configUtil';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';

const useStyles = makeStyles((theme) => ({
    category: {
        padding: '10px',
        backgroundColor: '#707070',
        fontStyle: 'italic',
        marginBottom: '10px',
        fontSize: 'small',
        color: '#fff',
        fontWeight: 'bold',
    },
    safeguard: {
        minWidth: '100%',
        maxWidth: '100%',
        minHeight: '90%',
        borderStyle: 'solid',
        borderWidth: '0.5px',
        borderColor: '#7c7c7c',
    },
    title: {
        fontWeight: 'bold',
        fontSize: 'large',
        padding: '10px',
        textAlign: 'center',
    },
    subTitle: {
        fontSize: '12px',
        fontWeight: 'bold',
        padding: '10px',
    },
    body: {
        fontSize: 'small',
    },
    infoBox: {
        color: 'black',
        fontSize: 'x-small',
        margin: '5px',
    },
    footer: {
        height: '60px',
        textAlign: 'center',
        fontWeight: 'bold',
    },
    greenTitle: {
        background: '#6aa84f',
    },
    amberTitle: {
        background: '#f1c232',
        color: 'black',
    },
    redTitle: {
        background: '#a61c00',
    },
    greenSubTitle: {
        background: '#93c47d',
        color: 'black',
    },
    amberSubTitle: {
        background: '#ffd966',
        color: 'black',
    },
    redSubTitle: {
        background: '#cc4125',
        color: 'black',
    },
    greenBody: {
        background: '#b6d7a8',
        color: 'black',
    },
    amberBody: {
        background: '#ffe599',
        color: 'black',
    },
    redBody: {
        background: '#dd7e6b',
        color: 'black',
    },
    greenSpacer: {
        background: '#d9ead3',
    },
    amberSpacer: {
        background: '#fff2cc',
    },
    redSpacer: {
        background: '#e6b8af',
    },
    radioButton: {
        marginLeft: '20px',
    },
    cpis: {
        border: '1px solid #C62828',
        padding: 2,
        backgroundColor: '#C62828',
        borderRadius: 5,
        color: '#fff',
        fontWeight: 'bold',
        fontSize: 'medium',
    },
}));

const PaediatricSafeguardingForm = (props) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const userObject = useSelector(user);
    const userFullName = Formatter.formatUserFullName(userObject);

    const ptVisits = useSelector(ctxCounterVisitsED);

    // Configs
    const alternativePAEDSSafeguarding = getConfig('alternativePAEDSSafeguarding', false);

    const scrollRef = useRef(null);
    const firstUpdate = useRef(true);

    // Data Form
    const [paediatricChecksForm, setPaediatricChecksForm] = useState({
        socialCare: null,
        socialCareName: '',
        socialCareContact: '',
        socialCareInfo: '',
        lookedAfterChild: null,
        lookedAfterChildInfo: '',
        agreement: null,
        agreementInfo: '',
        CPOCPIS: '',
        attendances: '',
    });
    const [safeguardingForm, setSafeguardingForm] = useState({
        level: '',
        reasons: {
            additionalInfo: false,
            safeguarding: [],
            additionalInfoDescription: '',
            infoNurse: false,
            reason: '',
            refCAMHS: false,
            refCHaD: false,
            refHealthVisitor: false,
            refOther: false,
            refOtherDescription: '',
            refPolice: false,
            trustSafeguarding: false,
        },
    });

    // Init Data
    useEffect(() => {
        if (props.selected) {
            setPaediatricChecksForm({...props.selected?.paediatric});
            props.levelIsSelected(!_.isEmpty(props.selected?.safeguarding?.level));
            setSafeguardingForm({...props.selected?.safeguarding});
        } else {
            setSafeguardingForm(prevState => ({
                ...prevState, reasons: {
                    ...prevState.reasons, safeguarding: getSafeguardingValues(props.patient),
                },
            }));
        }
    }, []);

    useEffect(() => {
        saveData();
    }, [safeguardingForm, paediatricChecksForm]);

    useEffect(() => {
        if (props.clerkingObj) {
            let sClerking = props.clerkingObj?.safeguarding ? props.clerkingObj.safeguarding : [];
            let sAll = getSafeguardingValues(props.patient);
            let __unique = _.uniqWith([...sClerking, ...sAll], _.isEqual);

            setSafeguardingForm(prevState => ({
                ...prevState, reasons: {
                    ...prevState.reasons, safeguarding: __unique,
                },
            }));
        }

    }, [props.clerkingObj]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({behavior: 'smooth'});
        }
    }, [safeguardingForm.level]);

    const getNote = () => {
        return Notes.getPaediatricSafeguardingNote(paediatricChecksForm, safeguardingForm, props.parentForm);
    };

    const saveData = () => {
        if (dispatch(hasPermission(Permissions.AGYLE_CAN_MANAGE_PAEDIATRIC_SAFEGUARDING))) {
            if (!firstUpdate.current) {
                const dateOfForm = moment().toISOString();
                const id = uuidv4() + '';
                const data = {
                    id: id,
                    paediatric: paediatricChecksForm,
                    safeguarding: safeguardingForm,
                    date: dateOfForm,
                    by: userFullName,
                    login: userObject?.userName,
                    parentForm: props.parentForm,
                };

                const dataToSave = [{
                    name: 'studies.paedSafeguarding',
                    value: data,
                    noteToAddWithRefId: getNote(),
                    referenceId: id,
                }];

                props.save(dataToSave);
            }
        }
    };

    const handleChange = (event, type) => {
        firstUpdate.current = false;
        const {name, value} = event.target;
        // eslint-disable-next-line default-case
        switch (type) {
            case 'paedCheck': {
                setPaediatricChecksForm(prevState => ({...prevState, [name]: value}));
                break;
            }
            case 'safeguarding': {
                setSafeguardingForm(prevState => ({...prevState, reasons: {...prevState.reasons, [name]: value}}));
                break;
            }
        }
    };

    const checkboxHandler = (event, type) => {
        firstUpdate.current = false;
        const {name, checked, value} = event.target;
        // eslint-disable-next-line default-case
        switch (type) {
            case 'paedCheck': {
                setPaediatricChecksForm(prevState => ({...prevState, [name]: value}));
                if (name === 'socialCare' && value === 'No') {
                    const data = {
                        socialCareContact: '',
                        socialCareName: '',
                    };
                    setPaediatricChecksForm(prevState => ({...prevState, ...data}));
                }
                break;
            }
            case 'safeguarding': {
                setSafeguardingForm(prevState => ({...prevState, reasons: {...prevState.reasons, [name]: checked}}));
                break;
            }
        }
    };

    const handleRadioChange = (event) => {
        firstUpdate.current = false;
        props.levelIsSelected(true);
        setSafeguardingForm(prevState => ({...prevState, level: event.target.value}));
    };

    // Render Paediatric Checks
    const paedCheckRender = () => {
        return (
            <div>
                {alternativePAEDSSafeguarding &&
                    <Grid container spacing={2} direction="row" justifyContent="flex-start" alignItems="center">
                        <Grid item xs={12}>
                            <Paper className={classes.category} elevation={3}>
                                {t('Does the child have a social worker?')}
                            </Paper>

                            <Grid
                                container
                                spacing={2}
                                direction="row"
                                justifyContent="flex-start"
                                alignItems="center"
                            >
                                <Grid item xs={12} md={1}>
                                    <FormControl component="fieldset" className={classes.radioButton}>
                                        <RadioGroup
                                            name="socialCare"
                                            value={paediatricChecksForm.socialCare}
                                            onChange={event => checkboxHandler(event, 'paedCheck')}
                                        >
                                            <FormControlLabel value="Yes" control={<Radio/>} label={t('Yes')}/>
                                            <FormControlLabel value="No" control={<Radio/>} label={t('No')}/>
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={11}>
                                    <Grid
                                        container
                                        spacing={2}
                                        direction="row"
                                        justifyContent="flex-end"
                                        alignItems="center"
                                    >
                                        {paediatricChecksForm.socialCare === 'Yes' &&
                                            <>
                                                <Grid item xs={12} md={11} style={{marginTop: 10}}>
                                                    <AgyleTextField
                                                        name="socialCareName"
                                                        label={t('Social Worker/Agency')}
                                                        onChange={event => handleChange(event, 'paedCheck')}
                                                        value={paediatricChecksForm.socialCareName}
                                                        error={!paediatricChecksForm.socialCareName}
                                                        fullWidth
                                                        size="large"
                                                        noBorder={false}
                                                        withNewBackground
                                                        debounce
                                                    />
                                                </Grid>
                                            </>
                                        }
                                        {paediatricChecksForm.socialCare === 'No' &&
                                            <Grid item xs={12} md={11}>
                                                <AgyleTextField
                                                    name="socialCareInfo"
                                                    label={t('Additional Info')}
                                                    onChange={event => handleChange(event, 'paedCheck')}
                                                    value={paediatricChecksForm.socialCareInfo}
                                                    fullWidth
                                                    size="large"
                                                    noBorder={false}
                                                    withNewBackground
                                                    debounce
                                                />
                                            </Grid>
                                        }
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <Paper className={classes.category} elevation={3}>
                                {t('Is this a looked after child?')}
                            </Paper>

                            <Grid
                                container
                                spacing={2}
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Grid item xs={12} md={1}>
                                    <Grid container direction="column" justifyContent="center" alignItems="center">
                                        <FormControl component="fieldset" className={classes.radioButton}>
                                            <RadioGroup
                                                name="lookedAfterChild"
                                                value={paediatricChecksForm.lookedAfterChild}
                                                onChange={event => checkboxHandler(event, 'paedCheck')}
                                            >
                                                <FormControlLabel value="Yes" control={<Radio/>} label={t('Yes')}/>
                                                <FormControlLabel value="No" control={<Radio/>} label={t('No')}/>
                                            </RadioGroup>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12} md={10}>
                                    <AgyleTextField
                                        name="lookedAfterChildInfo"
                                        label={t('Additional info')}
                                        onChange={event => handleChange(event, 'paedCheck')}
                                        value={paediatricChecksForm.lookedAfterChildInfo}
                                        fullWidth
                                        size="large"
                                        noBorder={false}
                                        withNewBackground
                                        debounce
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <Paper className={classes.category} elevation={3}>
                                {t('We routinely share information about all ED attendances with other health care providers. ' +
                                    'Sometimes, we also share information about attendances with local Social Care Team.')}
                            </Paper>

                            <Grid
                                container
                                spacing={1}
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Grid item xs={12} md={2}>
                                    <Grid container direction="column" justifyContent="center" alignItems="center">
                                        <FormControl component="fieldset" className={classes.radioButton}>
                                            <RadioGroup
                                                name="agreement"
                                                value={paediatricChecksForm.agreement}
                                                onChange={event => checkboxHandler(event, 'paedCheck')}
                                            >
                                                <FormControlLabel value="Yes" control={<Radio/>}
                                                                  label={t('Stated to Family')}/>
                                                <FormControlLabel value="No" control={<Radio/>}
                                                                  label={t('Not Stated to Family')}/>
                                            </RadioGroup>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12} md={10}>
                                    <AgyleTextField
                                        name="agreementInfo"
                                        label={t('Additional info')}
                                        onChange={event => handleChange(event, 'paedCheck')}
                                        value={paediatricChecksForm.agreementInfo}
                                        fullWidth
                                        size="large"
                                        noBorder={false}
                                        withNewBackground
                                        debounce
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <Paper className={classes.category} elevation={3}>
                                {t('Check any CPIS Information. ')}

                                {props.patient?.admission?.cpisFlag === 'Y' &&
                                    <span className={classes.cpis}>CPIS</span>
                                }
                            </Paper>

                            <Grid item xs={12}>
                                <AgyleTextField
                                    name="CPOCPIS"
                                    label={t('Additional info')}
                                    onChange={event => handleChange(event, 'paedCheck')}
                                    value={paediatricChecksForm.CPOCPIS}
                                    fullWidth
                                    size="large"
                                    noBorder={false}
                                    withNewBackground
                                    debounce
                                />
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <Paper className={classes.category} elevation={3}>
                                {t(`Review number of attendances in last 12 months - ED or other UEC | ED Visits last 12m: ${ptVisits || 0}`)}
                            </Paper>

                            <Grid item xs={12}>
                                <AgyleTextField
                                    name="attendances"
                                    label={t('Additional info')}
                                    onChange={event => handleChange(event, 'paedCheck')}
                                    value={paediatricChecksForm.attendances}
                                    fullWidth
                                    size="large"
                                    noBorder={false}
                                    withNewBackground
                                    debounce
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                }

                {!alternativePAEDSSafeguarding &&
                    <Grid container spacing={2} direction="row" justifyContent="flex-start" alignItems="center">
                        <Grid item xs={12}>
                            <Paper className={classes.category} elevation={3}>
                                {t('Question. Do you have any help from social care or any other agency?')}
                            </Paper>

                            <Grid
                                container
                                spacing={2}
                                direction="row"
                                justifyContent="flex-start"
                                alignItems="center"
                            >
                                <Grid item xs={12} md={1}>
                                    <FormControl component="fieldset" className={classes.radioButton}>
                                        <RadioGroup
                                            name="socialCare"
                                            value={paediatricChecksForm.socialCare}
                                            onChange={event => checkboxHandler(event, 'paedCheck')}
                                        >
                                            <FormControlLabel value="Yes" control={<Radio/>} label={t('Yes')}/>
                                            <FormControlLabel value="No" control={<Radio/>} label={t('No')}/>
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={11}>
                                    <Grid
                                        container
                                        spacing={2}
                                        direction="row"
                                        justifyContent="flex-end"
                                        alignItems="center"
                                    >
                                        {paediatricChecksForm.socialCare === 'Yes' &&
                                            <>
                                                <Grid item xs={12} md={5} style={{marginTop: 10}}>
                                                    <AgyleTextField
                                                        name="socialCareName"
                                                        label={t('Name Social Worker/Health Visitor')}
                                                        onChange={event => handleChange(event, 'paedCheck')}
                                                        value={paediatricChecksForm.socialCareName}
                                                        error={!paediatricChecksForm.socialCareName}
                                                        fullWidth
                                                        size="large"
                                                        noBorder={false}
                                                        withNewBackground
                                                        debounce
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={6} style={{marginTop: 10}}>
                                                    <AgyleTextField
                                                        name="socialCareContact"
                                                        label={t('Contact')}
                                                        type="number"
                                                        onChange={event => handleChange(event, 'paedCheck')}
                                                        value={paediatricChecksForm.socialCareContact}
                                                        error={!paediatricChecksForm.socialCareContact}
                                                        fullWidth
                                                        size="large"
                                                        noBorder={false}
                                                        withNewBackground
                                                        debounce
                                                    />
                                                </Grid>
                                            </>
                                        }

                                        <Grid item xs={12} md={11}>
                                            <AgyleTextField
                                                name="socialCareInfo"
                                                label={t('Additional Info')}
                                                onChange={event => handleChange(event, 'paedCheck')}
                                                value={paediatricChecksForm.socialCareInfo}
                                                fullWidth
                                                size="large"
                                                noBorder={false}
                                                withNewBackground
                                                debounce
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <Paper className={classes.category} elevation={3}>
                                {t('Question. Is this a looked after child?')}
                            </Paper>

                            <Grid
                                container
                                spacing={2}
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Grid item xs={12} md={1}>
                                    <Grid container direction="column" justifyContent="center" alignItems="center">
                                        <FormControl component="fieldset" className={classes.radioButton}>
                                            <RadioGroup
                                                name="lookedAfterChild"
                                                value={paediatricChecksForm.lookedAfterChild}
                                                onChange={event => checkboxHandler(event, 'paedCheck')}
                                            >
                                                <FormControlLabel value="Yes" control={<Radio/>} label={t('Yes')}/>
                                                <FormControlLabel value="No" control={<Radio/>} label={t('No')}/>
                                            </RadioGroup>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12} md={10}>
                                    <AgyleTextField
                                        name="lookedAfterChildInfo"
                                        label={t('Additional info')}
                                        onChange={event => handleChange(event, 'paedCheck')}
                                        value={paediatricChecksForm.lookedAfterChildInfo}
                                        fullWidth
                                        size="large"
                                        noBorder={false}
                                        withNewBackground
                                        debounce
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <Paper className={classes.category} elevation={3}>
                                {t('Question. We routinely share information about all ED attendances with other health care providers. ' +
                                    'Sometimes, we also share information about attendances with local Social Care Team. Do you consent to us doing this?')}
                            </Paper>

                            <Grid
                                container
                                spacing={2}
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Grid item xs={12} md={1}>
                                    <Grid container direction="column" justifyContent="center" alignItems="center">
                                        <FormControl component="fieldset" className={classes.radioButton}>
                                            <RadioGroup
                                                name="agreement"
                                                value={paediatricChecksForm.agreement}
                                                onChange={event => checkboxHandler(event, 'paedCheck')}
                                            >
                                                <FormControlLabel value="Yes" control={<Radio/>} label={t('Yes')}/>
                                                <FormControlLabel value="No" control={<Radio/>} label={t('No')}/>
                                            </RadioGroup>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12} md={10}>
                                    <AgyleTextField
                                        name="agreementInfo"
                                        label={t('Additional info')}
                                        onChange={event => handleChange(event, 'paedCheck')}
                                        value={paediatricChecksForm.agreementInfo}
                                        fullWidth
                                        size="large"
                                        noBorder={false}
                                        withNewBackground
                                        debounce
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <Paper className={classes.category} elevation={3}>
                                {t('Check any CIH and CPIS Information. ')}
                                {props.patient?.admission?.cpisFlag === 'Y' &&
                                    <span className={classes.cpis}>CPIS</span>
                                }
                            </Paper>

                            <Grid item xs={12}>
                                <AgyleTextField
                                    name="CPOCPIS"
                                    label={t('Additional info')}
                                    onChange={event => handleChange(event, 'paedCheck')}
                                    value={paediatricChecksForm.CPOCPIS}
                                    fullWidth
                                    size="large"
                                    noBorder={false}
                                    withNewBackground
                                    debounce
                                />
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <Paper className={classes.category} elevation={3}>
                                {t(`Review number of attendances in last 12 months - ED or other UEC | ED Visits last 12m: ${ptVisits || 0}`)}
                            </Paper>

                            <Grid item xs={12}>
                                <AgyleTextField
                                    name="attendances"
                                    label={t('Additional info')}
                                    onChange={event => handleChange(event, 'paedCheck')}
                                    value={paediatricChecksForm.attendances}
                                    fullWidth
                                    size="large"
                                    noBorder={false}
                                    withNewBackground
                                    debounce
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                }
            </div>
        );
    };

    // Render Safeguarding Level
    const safeguardingRender = () => {
        return (
            <div>
                <Paper className={classes.category} elevation={3}>
                    <Grid container direction="column" justifyContent="center" alignItems="center">
                        {t('Select Appropriate Safeguarding Level')}
                    </Grid>
                </Paper>

                {alternativePAEDSSafeguarding &&
                    <InformationBox type="info">
                        <Grid container direction="column" justifyContent="center" alignItems="center">
                            <Grid item xs={12}>
                                {t('Safeguarding Children in the Emergency Department/Paediatric Services')}
                            </Grid>
                            <p/>
                            <Grid item xs={12}>
                                {t('Everybody who works with children and young people have responsibility to safeguard them. ' +
                                    'We have a duty to promote the welfare of children and young people. ' +
                                    'Putting children at the center of our systems can ensure that the Trust is working together with the local Authority, ' +
                                    'CCG, Police and other services to safeguard children effectively.')}
                            </Grid>
                            <p/>
                            <Grid item xs={12}>
                                {t('Assess and reassess this situation and consider the guidance below. Consider Is this child safe for discharge? What is in the child\'s best interests?')}
                            </Grid>
                            <p/>
                            <Grid item xs={12}>
                                {t('We have a duty to share information and report to social care when there are safeguarding concerns and/or if significant risk of harm is suspected. ' +
                                    'Remember to follow hospital policy and procedure in all cases. If you are unsure contact: ' +
                                    'Mon-Thur 08:30 - 17:00 / Friday 08:30-16:45 - 01202 123334 (general line) / 01202 794209 (professionals line)' +
                                    'Mon - Thur 17:00 - 08:30 / Friday from 16:30 to Monday 08:30 / All bank holidays - 01202 738256 (Please request to speak to a Social worker of manager)')}
                            </Grid>
                        </Grid>
                    </InformationBox>
                }

                {!alternativePAEDSSafeguarding &&
                    <InformationBox type="info">
                        <Grid container direction="column" justifyContent="center" alignItems="center">
                            <Grid item xs={12}>
                                {t('Safeguarding Children in the Emergency Department/Paediatric Services')}
                            </Grid>
                            <p/>
                            <Grid item xs={12}>
                                {t('Everybody who works with children and young people have responsibility to safeguard them. ' +
                                    'We have a duty to promote the welfare of children and young people. ' +
                                    'Putting children at the center of our systems can ensure that the Trust is working together with the local Authority, ' +
                                    'CCG, Police and other services to safeguard children effectively.')}
                            </Grid>
                            <p/>
                            <Grid item xs={12}>
                                {t('Assess and reassess this situation and consider the guidance below. Consider Is this child safe for discharge? What is in the child\'s best interests?')}
                            </Grid>
                            <p/>
                            <Grid item xs={12}>
                                {t('We have a duty to share information and report to social care when there are safeguarding concerns and/or if significant risk of harm is suspected. ' +
                                    'Remember to follow hospital policy and procedure in all cases. If you are unsure contact the integrated safeguarding office for advice on ext 3274/4663 (0730-1600, Mon-Fri) ' +
                                    'or the children\'s safeguarding office on ext 4748 (0900-1700), or CHaD (Children\'s Advice and Duty Service for Dorset Council) on 01305 228558.')}
                            </Grid>
                        </Grid>
                    </InformationBox>
                }

                <FormControl>
                    <RadioGroup name="level" value={safeguardingForm.level} onChange={handleRadioChange}>
                        <Grid container spacing={1} alignItems="stretch" style={{marginBottom: 15}}>
                            <Grid item xs={12} md={4}>
                                <div className={clsx(classes.safeguard, classes.greenSpacer)}>
                                    <div className={clsx(classes.title, classes.greenTitle)}>
                                        {t('Safeguarding Level: Green')}
                                    </div>

                                    <div className={clsx(classes.subTitle, classes.greenSubTitle)}>
                                        {t('No safeguarding concerns')}
                                    </div>

                                    <div className={clsx(classes.body, classes.greenBody)}>
                                        <ul className={classes.greenBody}>
                                            <li>{t('History is consistent')}</li>
                                            <li>{t('No inappropriate delays in seeking treatment')}</li>
                                            <li>{t('Injury matches description and is appropriate for the developmental age of the child/young person')}</li>
                                            <li>{t('No other injuries or unexplained findings on examination')}</li>
                                            <li>{t('No concern about the interaction between the parent and the child/young person')}</li>
                                            <li>{t('No injury features suggestive of physical or sexual abuse')}</li>
                                            <li>{t('No findings consistent with neglect')}</li>
                                            <li>{t('No evidence of sexual activity in the child or young person (especially if underage)')}</li>
                                        </ul>
                                    </div>

                                    <div className={clsx(classes.subTitle, classes.greenSubTitle)}>
                                        {t('Information share as per normal protocol and guidance.')}
                                    </div>
                                </div>

                                <Grid container direction="row" justifyContent="center" alignItems="center">
                                    <Grid item md={12}>
                                        <div
                                            className={clsx(classes.footer, classes.greenTitle)}
                                            style={{paddingTop: 10}}
                                        >
                                            <FormControlLabel
                                                value="green"
                                                control={<Radio/>}
                                                label={t('Select This Option')}
                                            />
                                        </div>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {alternativePAEDSSafeguarding &&
                                <Grid item xs={12} md={3}>
                                    <div className={clsx(classes.safeguard, classes.amberSpacer)}>
                                        <div className={clsx(classes.title, classes.amberTitle)}>
                                            {t('Safeguarding Level: Amber')}
                                        </div>

                                        <div className={clsx(classes.subTitle, classes.amberSubTitle)}>
                                            {t('Potential safeguarding concerns')}
                                        </div>

                                        <div className={clsx(classes.body, classes.amberBody)}>
                                            <ul className={classes.amberBody}>
                                                <li>{t('Mobile child < 1 with head injury')}</li>
                                                <li>{t('Mobile child < 5 with a burn/scald')}</li>
                                                <li>{t('All accidental/potential ingestion toxic substances')}</li>
                                                <li>{t('Dog bite > 2yrs')}</li>
                                                <li>{t('Home safety/accident prevention')}</li>
                                                <li>{t('< 18 Drug/Alcohol misuse (refer to young alcohol service)')}</li>
                                                <li>{t('All reports - Bullying/anger management')}</li>
                                                <li>{t('Concerns about child\'s weight (under weight or overweight)')}</li>
                                                <li>{t('< 16 attending alone')}</li>
                                                <li>{t('3 or more attendances in 1 year')}</li>
                                                <li>{t('Did not wait to be seen')}</li>
                                                <li>{t('Unimmunised/incompletely immunised')}</li>
                                                <li>{t('Further support required')}</li>
                                            </ul>
                                        </div>
                                    </div>

                                    <Grid container direction="row" justifyContent="center" alignItems="center">
                                        <Grid item xs={12}>
                                            <div
                                                className={clsx(classes.footer, classes.amberTitle)}
                                                style={{paddingTop: 10}}
                                            >
                                                <FormControlLabel
                                                    value="amber"
                                                    control={<Radio/>}
                                                    label={t('Select This Option')}
                                                />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </Grid>}

                            {!alternativePAEDSSafeguarding &&
                                <Grid item xs={12} md={4}>
                                    <div className={clsx(classes.safeguard, classes.amberSpacer)}>
                                        <div className={clsx(classes.title, classes.amberTitle)}>
                                            {t('Safeguarding Level: Amber')}
                                        </div>

                                        <div className={classes.infoBox}>
                                            {t('If currently known to social care, young carers, ' +
                                                'or a child with a disability – consider sending a courtesy information phone call or email.')}
                                        </div>

                                        <div className={clsx(classes.subTitle, classes.amberSubTitle)}>
                                            {t('Potential safeguarding concerns')}
                                        </div>

                                        <div className={clsx(classes.body, classes.amberBody)}>
                                            <ul className={classes.amberBody}>
                                                <li>{t('Home and safety prevention – e.g., an accidental injury/burn/dog bite, considering context and seriousness.')}</li>
                                                <li>{t('Accidental ingestion of medicines or substances')}</li>
                                                <li>{t('Evidence/concerns of neglect– Refer to Health Visitor/School nurse early support')}</li>
                                                <li>{t('Mental health – self harm if regretful or no longer wishing to harm self – Refer to CAMHS')}</li>
                                                <li>{t('Substance/alcohol misuse – Refer to REACH')}</li>
                                                <li>{t('Teenage pregnancy or concerns about sexual activity – Refer to CERAT')}</li>
                                                <li>{t('Assaults/bullying/anger management')}</li>
                                                <li>{t('Delay in presentation – is parental or carer support or education needed?')}</li>
                                                <li>{t('Unexplained injuries or injuries inconsistent with history')}</li>
                                                <li>{t('Any other concerns not listed here.')}</li>
                                            </ul>
                                        </div>
                                    </div>

                                    <Grid container direction="row" justifyContent="center" alignItems="center">
                                        <Grid item xs={12}>
                                            <div
                                                className={clsx(classes.footer, classes.amberTitle)}
                                                style={{paddingTop: 10}}
                                            >
                                                <FormControlLabel
                                                    value="amber"
                                                    control={<Radio/>}
                                                    label={t('Select This Option')}
                                                />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </Grid>}

                            {alternativePAEDSSafeguarding &&
                                <Grid item xs={12} md={5}>
                                    <div className={clsx(classes.safeguard, classes.redSpacer)}>
                                        <div className={clsx(classes.title, classes.redTitle)}>
                                            {t('Safeguarding Level: Red')}
                                        </div>

                                        <div className={clsx(classes.subTitle, classes.redSubTitle)}>
                                            {t('In need of immediate protection.')}
                                        </div>

                                        <div className={clsx(classes.body, classes.redBody)}>
                                            <ul className={classes.redBody}>
                                                <li>{t('NON-MOBILE child any injury/oral/nasal bleeding, burn/fracture/bruising - THINK disability')}</li>
                                                <li>{t('Significant or life-threatening event')}</li>
                                                <li>{t('Unusual injury or inconsistent history')}</li>
                                                <li>{t('Child < 1 with long bone fracture * D/W CP Paeds')}</li>
                                                <li>{t('Dog bite < 2yrs')}</li>
                                                <li>{t('Unreasonable delay in presenting')}</li>
                                                <li>{t('Disclosed/alleged/potential NAl/abuse or neglect')}</li>
                                                <li>{t('All reports - Assault')}</li>
                                                <li>{t('Self harm/mental health/OD/reckless behaviour + refer to CAMHS')}</li>
                                                <li>{t('Child exploitation risk/gang activity/sexual activity in under 13\'s - complete sCERAT')}</li>
                                                <li>{t('Pregnancy < 13yrs')}</li>
                                                <li>{t('Any FGM < 18yrs (refer child if FM identified in mother/female family member) - REMEMBER: < 18years ALSO report via 101')}</li>
                                                <li>{t('Young carer')}</li>
                                                <li>{t('Domestic abuse/forced marriage/honour based violence')}</li>
                                                <li>{t('Parent/Carer with alcohol/drug misuse/MH/SHOD')}</li>
                                                <li>{t('Unborn baby with any of the above parent concerns')}</li>
                                                <li>{t('Child has a social worker/LAC/CP Plan/CPI flag/CP-IS alert')}</li>
                                            </ul>
                                        </div>
                                    </div>

                                    <Grid container direction="row" justifyContent="center" alignItems="center">
                                        <Grid item xs={12}>
                                            <div
                                                className={clsx(classes.footer, classes.redTitle)}
                                                style={{paddingTop: 10}}
                                            >
                                                <FormControlLabel
                                                    value="red"
                                                    control={<Radio/>}
                                                    label={t('Select This Option')}
                                                />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </Grid>}

                            {!alternativePAEDSSafeguarding &&
                                <Grid item xs={12} md={4}>
                                    <div className={clsx(classes.safeguard, classes.redSpacer)}>
                                        <div className={clsx(classes.title, classes.redTitle)}>
                                            {t('Safeguarding Level: Red')}
                                        </div>

                                        <div className={clsx(classes.subTitle, classes.redSubTitle)}>
                                            {t('In need of immediate protection.')}
                                        </div>

                                        <div className={clsx(classes.body, classes.redBody)}>
                                            <ul className={classes.redBody}>
                                                <li>{t('Any injury/bruise in a non-mobile child (follow Non-Mobile Child policy)')}</li>
                                                <li>{t('Child exploitation/sexual activity/gang activity')}</li>
                                                <li>{t('Deliberate harm/maltreatment/significant neglect')}</li>
                                                <li>{t('Mental health  – self harm if still suicidal or stating intent to harm self. Refer to CAMHS. ' +
                                                    'Discuss with senior nurse or doctor if high risk – consider admission.')}</li>
                                                <li>{t('Assault – refer to CERAT')}</li>
                                                <li>{t('Female Genital Mutilation/Domestic Violence/Forced Marriage/Honour-based violence')}</li>
                                                <li>{t('Parent or carer with DSH/OD or currently unable to care for the child due to intoxication or misuse of drugs.')}</li>
                                                <li>{t('Delay in presentation causing significant risk to the child.')}</li>
                                                <li>{t('Unborn baby at risk of harm.')}</li>
                                                <li>{t('Significant unexplained injuries or significant injuries inconsistent with history.')}</li>
                                                <li>{t('Any other concerns not listed here.')}</li>
                                            </ul>
                                        </div>
                                    </div>

                                    <Grid container direction="row" justifyContent="center" alignItems="center">
                                        <Grid item xs={12}>
                                            <div
                                                className={clsx(classes.footer, classes.redTitle)}
                                                style={{paddingTop: 10}}
                                            >
                                                <FormControlLabel
                                                    value="red"
                                                    control={<Radio/>}
                                                    label={t('Select This Option')}
                                                />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            }
                        </Grid>
                    </RadioGroup>
                </FormControl>

                {(safeguardingForm.level === 'amber' || safeguardingForm.level === 'red') &&
                    <div style={{marginTop: '30px'}} ref={scrollRef}>
                        <>
                            <Paper className={classes.category} elevation={3}>
                                {t('Reason for Selecting ')} {safeguardingForm.level === 'amber' ? t('Amber') : t('Red')}
                            </Paper>

                            <Grid item xs={12}>
                                <AgyleTextField
                                    name="reason"
                                    label={t('Reason')}
                                    onChange={event => handleChange(event, 'safeguarding')}
                                    value={safeguardingForm.reasons.reason || ''}
                                    error={!safeguardingForm.reasons.reason}
                                    fullWidth
                                    size="large"
                                    noBorder={false}
                                    withNewBackground
                                    debounce
                                />
                            </Grid>

                            <Grid item xs={12} style={{margin: '10px 0'}}>
                                <Safeguarding
                                    label={t('Safeguarding')}
                                    color="primary"
                                    value={safeguardingForm.reasons.safeguarding}
                                    onChange={event => {
                                        firstUpdate.current = false;
                                        setSafeguardingForm((pr) => ({
                                            ...pr,
                                            reasons: {...pr.reasons, safeguarding: event},
                                        }));
                                    }}
                                    admitDate={props.patient?.admission?.startDate}
                                    fullWidth
                                    withBorder
                                />
                            </Grid>
                        </>

                        <>
                            <Paper className={classes.category} elevation={3}>
                                {t('Action Taken. Select all that apply')}
                            </Paper>

                            <Grid
                                container
                                spacing={2}
                                direction="row"
                                justifyContent="space-around"
                                alignItems="center"
                            >
                                <Grid item xs={12} md={6}>
                                    <Grid container direction="column" justifyContent="center" alignItems="flex-start">
                                        <FormControlLabel
                                            style={{marginLeft: '20%'}}
                                            control={
                                                <Checkbox
                                                    checked={safeguardingForm.reasons.infoNurse || false}
                                                    onChange={event => checkboxHandler(event, 'safeguarding')}
                                                    name="infoNurse"
                                                />
                                            }
                                            label={t('Informed Nurse in Charge')}
                                        />
                                        <FormControlLabel
                                            style={{marginLeft: '20%'}}
                                            control={
                                                <Checkbox
                                                    checked={safeguardingForm.reasons.trustSafeguarding || false}
                                                    onChange={event => checkboxHandler(event, 'safeguarding')}
                                                    name="trustSafeguarding"
                                                />
                                            }
                                            label={t('Discussed with: Trust Safeguarding Team')}
                                        />
                                        <FormControlLabel
                                            style={{marginLeft: '20%'}}
                                            control={
                                                <Checkbox
                                                    checked={safeguardingForm.reasons.refHealthVisitor || false}
                                                    onChange={event => checkboxHandler(event, 'safeguarding')}
                                                    name="refHealthVisitor"
                                                />
                                            }
                                            label={t('Referral Requested: Health Visitor/Early Help/School Nurse')}
                                        />
                                        <FormControlLabel
                                            style={{marginLeft: '20%'}}
                                            control={
                                                <Checkbox
                                                    checked={safeguardingForm.reasons.refCHaD || false}
                                                    onChange={event => checkboxHandler(event, 'safeguarding')}
                                                    name="refCHaD"
                                                />
                                            }
                                            label={t('Referral to: CHaD')}
                                        />
                                    </Grid>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Grid container direction="column" justifyContent="center" alignItems="flex-start">
                                        <FormControlLabel
                                            style={{marginLeft: '20%'}}
                                            control={
                                                <Checkbox
                                                    checked={safeguardingForm.reasons.refCAMHS || false}
                                                    onChange={event => checkboxHandler(event, 'safeguarding')}
                                                    name="refCAMHS"
                                                />
                                            }
                                            label={t('Referral to: CAMHS')}
                                        />
                                        <FormControlLabel
                                            style={{marginLeft: '20%'}}
                                            control={
                                                <Checkbox
                                                    checked={safeguardingForm.reasons.refOther || false}
                                                    onChange={event => checkboxHandler(event, 'safeguarding')}
                                                    name="refOther"
                                                />
                                            }
                                            label={t('Referral to: Other (Free Text)')}
                                        />
                                        <FormControlLabel
                                            style={{marginLeft: '20%'}}
                                            control={
                                                <Checkbox
                                                    checked={safeguardingForm.reasons.refPolice || false}
                                                    onChange={event => checkboxHandler(event, 'safeguarding')}
                                                    name="refPolice"
                                                />
                                            }
                                            label={t('Referral to: Police')}
                                        />
                                        <FormControlLabel
                                            style={{marginLeft: '20%'}}
                                            control={
                                                <Checkbox
                                                    checked={safeguardingForm.reasons.additionalInfo || false}
                                                    onChange={event => checkboxHandler(event, 'safeguarding')}
                                                    name="additionalInfo"
                                                />
                                            }
                                            label={t('Additional Referral Information')}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </>

                        {safeguardingForm.reasons.refOther &&
                            <>
                                <Paper className={classes.category} elevation={3}>
                                    {t('Referral to Other')}
                                </Paper>

                                <Grid item xs={12} style={{marginBottom: 10}}>
                                    <AgyleTextField
                                        name="refOtherDescription"
                                        label={t('Description')}
                                        onChange={event => handleChange(event, 'safeguarding')}
                                        value={safeguardingForm.reasons.refOtherDescription || ''}
                                        fullWidth
                                        size="large"
                                        noBorder={false}
                                        withNewBackground
                                        debounce
                                    />
                                </Grid>
                            </>
                        }

                        {safeguardingForm.reasons.additionalInfo &&
                            <>
                                <Paper className={classes.category} elevation={3}>
                                    {t('Additional Referral Information')}
                                </Paper>

                                <Grid item xs={12}>
                                    <AgyleTextField
                                        name="additionalInfoDescription"
                                        label={t('Description')}
                                        onChange={event => handleChange(event, 'safeguarding')}
                                        value={safeguardingForm.reasons.additionalInfoDescription || ''}
                                        fullWidth
                                        size="large"
                                        noBorder={false}
                                        withNewBackground
                                        debounce
                                    />
                                </Grid>
                            </>
                        }
                    </div>
                }
            </div>
        );
    };

    return (
        <>
            <Paper sx={{p: 1, mb: 1, textAlign: 'center'}}>
                <Typography variant="h5" noWrap component="div">
                    {t('Paediatric Checks')}
                </Typography>
            </Paper>

            {paedCheckRender()}

            <Paper sx={{p: 1, mb: 1, textAlign: 'center'}}>
                <Typography variant="h5" noWrap component="div">
                    {t('Safeguarding Level')}
                </Typography>
            </Paper>

            {safeguardingRender()}
        </>
    );
};

export default PaediatricSafeguardingForm;